<template>
  <el-form-item :label="label">
    <el-select
      :value="value"
      :multiple="multiple"
      filterable
      remote
      :remote-method="onSearch"
      :loading="searching"
      :clearable="clearable"
      :collapse-tags="multiple"
      :multiple-limit="multipleLimit"
      :disabled="disabled"
      style="width: 100%"
      size="small"
      @input="onInput"
      @visible-change="onVisibleChange"
    >
      <el-option v-for="item in options" :key="item.id" :label="item.name" :value="item.id">
        <div class="option__row">
          <span class="option__name">
            <el-checkbox v-if="multiple" :value="isSelected(item.id)" />
            {{ item.name }}
          </span>
          <span v-if="showQuota && item.freely_cameras_count != null" class="option__quota">
            <fa :icon="faVideo" /> {{ item.freely_cameras_count }}
          </span>
        </div>
      </el-option>
      <el-option v-if="hasMore" :value="LOAD_MORE_CONST" disabled>
        <el-button type="text" size="mini" style="width: 100%" :loading="loadingMore" @click="loadMore">
          {{ loadingMore ? "Загрузка" : "Показать ещё" }}
        </el-button>
      </el-option>
    </el-select>
  </el-form-item>
</template>
<script>
import { FontAwesomeIcon as fa } from "@fortawesome/vue-fontawesome";
import { faVideo } from "@fortawesome/free-solid-svg-icons";

const PAGE_SIZE = 20;
const LOAD_MORE_CONST = "__load_more__";

export default {
  name: "ServerSelect",
  components: { fa },
  props: {
    value: {
      type: [Array, Number, String],
      default: null,
    },
    source: {
      type: Object,
      required: true,
    },
    label: {
      type: String,
      default: "",
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    multipleLimit: {
      type: Number,
      default: 0,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showQuota: {
      type: Boolean,
      default: false,
    },
    // Уже выбранные серверы с названиями. На редактировании их может не оказаться
    // на загруженных страницах — без этого вместо названия в поле был бы id.
    selectedItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      faVideo,
      LOAD_MORE_CONST,
      items: [],
      known: {},
      query: "",
      page: 1,
      total: 0,
      searching: false,
      loadingMore: false,
      requestId: 0,
    };
  },
  computed: {
    selectedIds() {
      if (this.multiple) return this.value || [];
      return this.value === null || this.value === "" ? [] : [this.value];
    },
    options() {
      const loadedIds = new Set(this.items.map((item) => item.id));
      const missing = this.selectedIds
        .filter((id) => !loadedIds.has(id))
        .map((id) => this.known[id])
        .filter(Boolean);
      return [...missing, ...this.items];
    },
    hasMore() {
      return this.page * PAGE_SIZE < this.total;
    },
  },
  watch: {
    selectedItems: {
      immediate: true,
      handler(items) {
        this.remember(items);
      },
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    remember(items) {
      const byId = {};
      items.forEach((item) => {
        byId[item.id] = item;
      });
      this.known = { ...this.known, ...byId };
    },
    isSelected(id) {
      return this.selectedIds.includes(id);
    },
    onInput(value) {
      // Очистка одиночного селекта отдаёт пустую строку — приводим к null, как в исходном состоянии.
      this.$emit("input", !this.multiple && value === "" ? null : value);
    },
    onSearch(query) {
      if (query === this.query) return;
      this.query = query;
      this.load();
    },
    onVisibleChange(visible) {
      // Закрытый селект стирает текст поиска — возвращаем и полный список.
      if (!visible && this.query) {
        this.query = "";
        this.load();
      }
    },
    load() {
      const requestId = ++this.requestId;
      this.page = 1;
      this.searching = true;
      return this.fetchPage(requestId, false)
        .catch(() => this.notifyError(requestId))
        .finally(() => {
          if (requestId === this.requestId) this.searching = false;
        });
    },
    loadMore() {
      const requestId = ++this.requestId;
      this.page += 1;
      this.loadingMore = true;
      return this.fetchPage(requestId, true)
        .catch(() => {
          // Если за это время начался новый поиск, страницу уже сбросили — откатывать нечего.
          if (requestId !== this.requestId) return;
          this.page -= 1;
          this.notifyError(requestId);
        })
        .finally(() => {
          this.loadingMore = false;
        });
    },
    fetchPage(requestId, append) {
      return this.source
        .fetch({ search: encodeURI(this.query), page: this.page, count: PAGE_SIZE })
        .then(({ data }) => {
          // Пока шёл запрос, поиск успели поменять — ответ устарел.
          if (requestId !== this.requestId) return;
          const items = (data.data || []).map(this.source.mapItem);
          this.remember(items);
          this.total = data.total || 0;
          this.items = append ? this.dedupe([...this.items, ...items]) : items;
        });
    },
    notifyError(requestId) {
      if (requestId !== this.requestId) return;
      this.$message({ type: "error", message: "Не удалось загрузить список серверов" });
    },
    dedupe(items) {
      const seen = new Set();
      return items.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
    },
  },
};
</script>
<style scoped>
.option__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
.option__name ::v-deep .el-checkbox {
  pointer-events: none;
  margin-right: 8px;
}
.option__quota {
  flex-shrink: 0;
  margin-left: 12px;
  color: #8492a6;
  font-size: 13px;
}
</style>
